import { Button, Modal } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useState } from 'react';
import type { ValidationErrors } from '@frontend/utils/validation';
import { accountRequestRejectSchema } from '@common/validation/accountRequest';
import { getSelectedLanguage } from '@frontend/translate';
import { useAccountRequestContext } from '@frontend/utils/hooks/useAccountRequestContext';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { AccountRequestRejectionReasonInput } from '../AccountRequestRejectionReasonInput';

interface AccountRequestDetailsRejectionReasonModalProps {
    isOpen: boolean;
    onCancel: () => void;
}

export function AccountRequestRejectionReasonModal(props: Readonly<AccountRequestDetailsRejectionReasonModalProps>) {
    const { accountRequest, rejectAccountRequest, getScrollToId } = useAccountRequestContext();
    const navigate = useNavigate();
    const language = getSelectedLanguage();

    const [rejectionReason, setRejectionReason] = useState<string>('');
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => {}}
            size="medium"
        >
            <Modal.Header>{language.accountRequest.declineAccountRequest}</Modal.Header>
            <Modal.Body>
                <AccountRequestRejectionReasonInput
                    errors={errors}
                    layout="horizontal"
                    onChange={handleChangeRejectionReason}
                    rejectionReason={rejectionReason}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={props.onCancel}
                    variant="outline"
                >
                    {language.common.cancel}
                </Button>
                <Button onClick={handleReject}>{language.common.save}</Button>
            </Modal.Footer>
        </Modal>
    );

    function handleChangeRejectionReason(nextRejectionReason: string) {
        setRejectionReason(nextRejectionReason);

        const validationErrors = updateValidationErrorsForOne(
            accountRequestRejectSchema,
            { rejectionReason: nextRejectionReason },
            errors,
            'rejectionReason'
        );

        setErrors(validationErrors);
    }

    async function handleReject() {
        const { errors: validationErrors } = validate(accountRequestRejectSchema, {
            rejectionReason,
        });

        setErrors(validationErrors);

        if (validationErrors.isEmpty()) {
            await rejectAccountRequest(rejectionReason);

            const scrollToId = getScrollToId(accountRequest);

            navigate(`/organizations/${accountRequest.organizationId}/view#${scrollToId}`);
        }
    }
}
